import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

function formatServiceDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function serviceStatusBadgeVariant(serviceStatus) {
  if (serviceStatus === 'overdue') {
    return {
      className: 'border-red-500/50 bg-red-500/15 text-red-400',
    };
  }
  if (serviceStatus === 'due_soon') {
    return {
      className: 'border-amber-500/50 bg-amber-500/15 text-amber-400',
    };
  }
  if (serviceStatus === 'no_record') {
    return {
      className: 'border-slate-500/40 bg-slate-500/10 text-slate-400',
    };
  }
  return {
    className: 'border-slate-500/40 bg-slate-500/10 text-slate-400',
  };
}

function serviceTypeLabel(type, t) {
  const key = String(type || '').toLowerCase();
  const map = {
    routine: t('serviceTypeRoutine'),
    major: t('serviceTypeMajor'),
    breakdown: t('serviceTypeBreakdown'),
    pdi: t('serviceTypePdi'),
    other: t('serviceTypeOther'),
  };
  return map[key] || type || '—';
}

export default function MachineServicePanel({ authToken }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const serviceStatusLabelFor = useCallback((serviceStatus) => {
    if (serviceStatus === 'overdue') return t('serviceOverdue');
    if (serviceStatus === 'due_soon') return t('serviceDueSoon');
    if (serviceStatus === 'no_record') return t('adminServiceNoRecord');
    return serviceStatus;
  }, [t]);

  const load = useCallback(async () => {
    if (!authToken) {
      setItems([]);
      setError(t('failedToLoad'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/machines/service-alerts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setItems([]);
      setError(err.message || t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card className="mb-6 w-full min-w-0 rounded-2xl border-de-gold/30 bg-gradient-to-br from-[#122a4a]/60 via-[#0a1628]/95 to-[#060e1c] de-glow-gold shadow-2xl">
      <CardHeader className="p-4 pb-2 sm:p-5">
        <CardTitle className="text-base font-semibold text-[#C9A84C]">
          {String.fromCodePoint(0x1F527)} {t('adminServiceAlertsTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-3 p-4 pt-0 sm:p-5 sm:pt-0">
        {loading && (
          <p className="text-sm text-slate-400">{t('loading')}</p>
        )}

        {!loading && error && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-400">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              className="border-de-gold/40 text-de-gold hover:bg-de-gold/10"
            >
              {t('retryLabel')}
            </Button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-emerald-400">{t('adminServiceAllOk')}</p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    t('complianceColMachine'),
                    t('complianceColOwner'),
                    t('adminServiceColLastService'),
                    t('adminServiceColNextDue'),
                    t('adminServiceColServiceStatus'),
                    t('status'),
                  ].map((h) => (
                    <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const badge = serviceStatusBadgeVariant(row.serviceStatus);
                  const lastService = row.last_service_date
                    ? `${formatServiceDate(row.last_service_date)}${row.last_service_type ? ` — ${serviceTypeLabel(row.last_service_type, t)}` : ''}`
                    : '—';
                  const nextDue = row.next_service_date
                    ? formatServiceDate(row.next_service_date)
                    : (row.next_service_hmr != null
                      ? `${row.next_service_hmr} ${t('hmrHrs')}`
                      : '—');
                  return (
                    <TableRow key={`${row.machine_id}-${row.owner_name}`}>
                      <TableCell>
                        <Badge>{row.machine_id}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-300">
                        {row.owner_name || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-200">
                        {lastService}
                      </TableCell>
                      <TableCell className="text-sm text-slate-200">
                        {nextDue}
                      </TableCell>
                      <TableCell>
                        <Badge className={badge.className}>
                          {serviceStatusLabelFor(row.serviceStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-300">
                        {row.status || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
